import { Flex, usePlatformProps } from '@acsbe/ui';
import { useMemo } from 'react';
import { styled } from 'styled-components';
import { useApplicationViewState } from '../../../context';
import { PlanSummary } from './PlanSummary';
import { LicenseDetails, PlanType, PricingModel } from './types';

export type BillingCycle = 'annually' | 'monthly' | null;

export interface PlanComponentProps {
  plan: PlanType;
  billingCycle: BillingCycle;
  setMargin?: boolean;
  license?: LicenseDetails;
  pageCount?: number;
}

export const Plan = ({
  plan,
  billingCycle,
  setMargin = false,
  license,
  pageCount,
}: PlanComponentProps) => {
  const p = usePlatformProps();
  const { showPlanComparisonView } = useApplicationViewState();
  // Calculate whether this plan comes under trial based on page count or is subscribed
  const { isCurrent, isRenew, isUpgrade, isContact, isReactivate } =
    useMemo(() => {
      const data = {
        isCurrent: false,
        isRenew: false,
        isReactivate: false,
        isUpgrade: false,
        isContact: false,
      };
      if (!license || !plan || !license.plan?.id) {
        return data;
      }

      const isSamePlan =
        license?.plan?.id === plan.id || license?.plan?.name === plan.name;
      const isMonthly = license?.monthly;
      const isActive = license?.status === 'active';
      const isExpired = license?.status === 'expired';
      const isGrace = license?.status === 'grace';
      const isCancelled = license?.status === 'cancelled';

      if (isSamePlan) {
        if (billingCycle === 'monthly' && !isMonthly && !isGrace) {
          data.isContact = true;
          return data;
        }

        if (billingCycle === 'annually' && isMonthly && !isGrace) {
          data.isUpgrade = true;
          return data;
        }

        data.isCurrent = isActive;
        data.isRenew = isGrace || isExpired;
        data.isReactivate = isCancelled;

        return data;
      }

      if (isActive || isGrace || isExpired || isCancelled) {
        if (plan.tier) {
          if (license?.plan?.tier > plan.tier) {
            data.isContact = true;
            data.isUpgrade = false;
          } else if (license?.plan?.tier < plan.tier) {
            data.isUpgrade = true;
            data.isContact = false;
          }
          return data;
        }
      }

      return data;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [license, pageCount, plan, billingCycle]);

  return (
    <BorderedFlex
      self="stretch"
      justify="start"
      stretch
      radius="xl"
      showBorder={!showPlanComparisonView}
      marginInlineStart={setMargin ? 3 : 0}
      //@ts-ignore
      setOpacity={
        pageCount !== undefined &&
        plan.pricingModelPlan === PricingModel.pageCount &&
        pageCount > plan.limitation
      }
    >
      <PlanSummary
        isCurrent={isCurrent}
        isRenew={isRenew}
        isUpgrade={isUpgrade}
        isContact={isContact}
        isReactivate={isReactivate}
        plan={plan}
        billingCycle={billingCycle}
        pageCount={pageCount}
      />
    </BorderedFlex>
  );
};

export const BorderedFlex = styled(Flex)<{ showBorder?: boolean }>`
  border: ${(props) => (props.showBorder ? '1.5px solid #E8EBF3' : 'none')};
`;
